import { useCallback, useEffect, useRef, useState } from 'react'
import { Keyboard, Platform, StyleSheet } from 'react-native'

import { useNavigation } from '@react-navigation/native'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import { colors as zetColors } from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import { Button } from '@zet/design-system/button'
import { Stack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import ArrowLeft from '@zet/design-system/svg/ArrowLeft'
import { B2, H4, SH3, Text } from '@zet/design-system/typography'
import storageUtil from '@zet/utils/storage'
import Lottie from "lottie-react"
import LottieView from 'lottie-react-native'
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field'
import RNOtpVerify from 'react-native-otp-verify'

import {
  useGetDsaList,
  useSubmitSelectedDsa,
  useVerifyOtpBridge,
  useVerifyPhoneNumberBridge,
} from '../../../api/login/hooks'
import { bridgeApi } from '../../../api/network'
import { i18n } from '../../../localization'
import useAuthStore from '../../../store/authStore'

const OTP_COUNT = 4

const VerifyOtp = ({ route }) => {
  const isWeb = Platform.OS === 'web'
  const [value, setValue] = useState<string>('')
  const [timer, setTimer] = useState<number>(30)
  const [error, setError] = useState<string>('')
  const [resendSuccess, setResendSuccess] = useState<boolean>(false)

  const { setIsAuthenticated } = useAuthStore((store) => store)

  const { locale = 'en' } = bridgeApi.defaults.headers.common

  const { phoneNumber = '' } = route?.params || {}

  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  })

  const interval = 1000
  const timerRef = useRef(0)
  let prev = 0
  let diff = 0
  const timerCallback = () => {
    const date = new Date()
    setTimer((i) => {
      const current = date.getSeconds()
      if (diff === 0) {
        diff = 1
        prev = date.getSeconds()
        return 30
      }

      if (prev > current) diff = 60 - prev + current
      else diff = current - prev

      prev = date.getSeconds()

      if (i > 1)
        if (i + diff < 1) {
          clearInterval(timerRef.current)
          return 1
        } else {
          return i - diff
        }
      else {
        clearInterval(timerRef.current)
        return 0
      }
    })
  }
  const timerFunction = () => {
    timerRef.current = setInterval(timerCallback, interval)
  }

  const otpTimer = useCallback(timerFunction, [timerRef])

  useEffect(() => {
    otpTimer()
    return () => {
      clearInterval(timerRef.current)
    }
  }, [])

  const ref = useBlurOnFulfill({ value, cellCount: OTP_COUNT })
  const navigation = useNavigation()

  const { mutate, isLoading, isSuccess, data } = useVerifyOtpBridge()
  const {
    mutate: sendOtpMutate,
    isSuccess: sendOtpSuccess,
    data: sendOtpData,
  } = useVerifyPhoneNumberBridge()

  const otpHandler = useCallback(
    (message: string) => {
      if (message) {
        const otp: string | undefined = /(\d{4})/g.exec(message)?.[1]
        if (otp) {
          setValue(otp)
          mutate({ phoneNumber, value: otp })
        }
      }
    },
    [mutate, phoneNumber]
  )

  useEffect(() => {
    if(Platform.OS !== 'web') {

      RNOtpVerify.getOtp()
      .then((p) => RNOtpVerify.addListener(otpHandler))
      .catch((p) => console.log(p))
      
      return () => RNOtpVerify.removeListener()
    }
  }, [otpHandler])

  const handleVerify = () => {
    mutate({ phoneNumber: phoneNumber, password: value })
  }
  const { refetch, data: dsaList, isFetching: dsaListLoading } = useGetDsaList()

  const { mutate: mutateSelectedDsaData, data: selectedDsaData } =
    useSubmitSelectedDsa()

  useEffect(() => {
    if (isSuccess) {
      if (data?.error) {
        setError(data?.error)
      } else {
        bridgeApi.defaults.headers.common = {
          ...bridgeApi.defaults.headers.common,
          Authorization: `Bearer ${data?.jwtToken}`,
          Accept: 'application/json',
          locale: i18n.locale,
        }
        refetch()
      }
    }
  }, [isSuccess])

  useEffect(() => {
    if (sendOtpData?.error === null) {
      setResendSuccess(true)
    } else if (sendOtpData?.error) {
      setError(sendOtpData?.error)
    }
  }, [sendOtpSuccess])

  useEffect(() => {
    if (dsaList) {
      if (dsaList?.length === 1) {
        mutateSelectedDsaData(dsaList?.[0]?.dsaId)
      } else {
        navigation.replace('MultipleAccount', {
          handleBack: false,
        })
      }
    }
  }, [dsaList])

  useEffect(() => {
    async function navigator() {
      if (selectedDsaData && selectedDsaData?.jwtToken) {
        bridgeApi.defaults.headers.common = {
          ...bridgeApi.defaults.headers.common,
          Authorization: `Bearer ${selectedDsaData?.jwtToken}`,
          Accept: 'application/json',
          locale: i18n.locale,
        }
        if (selectedDsaData?.action === 'SHOW_BRIDGE_ONBOARDING_SCREEN') {
          navigation.replace('UserDetails', {
            selectedDsaData: selectedDsaData?.actionData,
            dsaList: dsaList,
            token: selectedDsaData?.jwtToken,
          })
        } else if (selectedDsaData?.action === 'SHOW_BRIDGE_HOME_SCREEN') {
          storageUtil.saveObject('token', selectedDsaData?.jwtToken)
          setIsAuthenticated(true)
          navigation.replace('HomeView')
        }
      }
    }
    navigator()
  }, [selectedDsaData])

  const onPressResend = () => {
    prev = 0
    diff = 0
    timerFunction()
    setResendSuccess(false)
    setError('')
    sendOtpMutate({ phoneNumber: phoneNumber })
  }

  const onChangeText = (text: string) => {
    // Test for the string to contain only numbers
    const reg = /^[0-9]+$/
    if (reg.test(text) || text === '') {
      setError('')
      setResendSuccess(false)
      setValue(text)
    }
  }

  return (
    <KeyBoardWrapper
      className=" flex-1 bg-white px-3"
      BottomSheet={
        <>
          <Stack className="mb-3">
            {error && <Alert intent={'error'}>{error}</Alert>}
            {resendSuccess && (
              <Alert intent={'primary'}>{sendOtpData?.message}</Alert>
            )}
          </Stack>
          <Button
            className="w-full"
            size="lg"
            onPress={handleVerify}
            // onPress={() => navigation.navigate('UserDetailsBridge')}
            onTouchStart={Keyboard.dismiss}
            loading={isLoading || dsaListLoading}
            disabled={value.length === 4 ? false : true}
          >
            {i18n.t('otpInput.verify')}
          </Button>

          {timer === 0 ? (
            <Stack className=" my-3 items-center ">
              <Pressable onPress={onPressResend}>
                <SH3 className="text-primary-100">
                  {i18n.t('otpInput.resend')}
                </SH3>
              </Pressable>
            </Stack>
          ) : (
            <Stack
              className="items-center justify-center"
              style={{
                flexDirection: locale === 'hi' ? 'row-reverse' : 'row',
              }}
            >
              <B2 className="text-grays-60 my-3 text-center">
                {i18n.t('otpInput.resendOtpIn')}
              </B2>
              <SH3 className="text-primary-100">
                00:{timer < 10 ? `0${timer}` : timer}
              </SH3>
            </Stack>
          )}
        </>
      }
    >
      <Stack>
        <Stack className="h-3" />
        <Pressable
          className="bg-grays-30 mb-2 h-5 w-5 items-center justify-center rounded"
          onPress={() => navigation.goBack()}
        >
          <ArrowLeft color={zetColors.grays[80]} />
        </Pressable>
        <Stack className="mt-8 items-center justify-center">
          <Stack className={`${isWeb && 'h-14 w-14'}`}>
            {isWeb ? <Lottie animationData={require('../../../../assets/lottie/SMSVerification.json')} /> : 
            <LottieView
              autoPlay
              className=" h-[108] w-[108] self-center "
              source={require('../../../../assets/lottie/SMSVerification.json')}
            /> }
          </Stack>
          <H4 className="mt-[28] text-center">{i18n.t('otpInput.enter')}</H4>
          <SH3 className=" text-grays-60 mt-1 text-center ">
            {/* {i18n.t('otpInput.sent', { number: mobileNumber.slice(2) })} */}
            {i18n.t('otpInput.sent', { number: phoneNumber })}
          </SH3>
        </Stack>

        <CodeField
          ref={ref}
          value={value}
          {...props}
          onChangeText={onChangeText}
          rootStyle={styles.codeFieldRoot}
          caretHidden={true}
          cellCount={OTP_COUNT}
          keyboardType="number-pad"
          renderCell={({ index, symbol, isFocused }) => (
            <Stack style={[styles.cell, isFocused && styles.focusCell]}>
              <Text key={index} onLayout={getCellOnLayoutHandler(index)}>
                {symbol || (isFocused ? <Cursor /> : null)}
              </Text>
            </Stack>
          )}
        />
      </Stack>
    </KeyBoardWrapper>
  )
}

export default VerifyOtp

const styles = StyleSheet.create({
  codeFieldRoot: {
    marginTop: 38,
    marginHorizontal: 58,
    alignItems: 'center',
    marginBottom: 20,
  },
  cell: {
    width: 48,
    height: 48,
    fontSize: 16,
    fontWeight: '500',
    borderWidth: 2,
    borderColor: zetColors.grays[40],
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    backgroundColor: '#F9F9FB',
  },
  focusCell: {
    borderColor: zetColors.primary[50],
  },
})
